import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Layout, Alert } from '../../../components';
import { SummaryWrapper } from '../../../components/SummaryWrapper';
import { DiscoveryInventoryContainer } from './components/DiscoveryInventoryContainer';
import { DiscoveryInventoryDrawer } from './components/DiscocoveryInventoryDrawer';
import { useUploadDetection } from './hooks/useUploadDetection';
import { discoveryInventoryApi } from '../../../api/discovery';
import { NotificationsPanel } from '../../../components/NotificationsPanel';
import { useNotificationsPanel } from '../../../providers/NotificationsPanelProvider';
import classNames from 'classnames';

const { Sider } = Layout;

const NEEDS_OPTIMIZATION_FILTERS = ['oversized_cpu', 'undersized_cpu', 'oversized_ram', 'undersized_ram'];

import './Inventory.scss';

export const Inventory = () => {
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);
    const location = useLocation();

    const initialFiltersFromLocation = useMemo(() => {
        if (location.state?.filter === 'needs_optimization') {
            return [...NEEDS_OPTIMIZATION_FILTERS];
        }
        if (Array.isArray(location.state?.filters)) {
            return location.state.filters;
        }
        return [];
    }, [location.state]);

    const [cardFilters, setCardFilters] = useState(() => initialFiltersFromLocation);
    const tableRef = useRef(null);

    const scrollToTable = useCallback(() => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    useEffect(() => {
        setCardFilters(initialFiltersFromLocation);
        if (location.state?.filters !== undefined || location.state?.filter !== undefined) {
            setTimeout(scrollToTable, 100);
        }
    }, [initialFiltersFromLocation, location.state, scrollToTable]);

    const handleCardFilterClick = useCallback(
        (filters) => {
            setCardFilters(filters);
            setTimeout(scrollToTable, 100);
        },
        [scrollToTable],
    );

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);

    const { notifications, isLoading: isNotificationsLoading } = useNotificationsPanel();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const activeImportId = location.state?.active_import_id ?? null;
    const hasPendingImport = !!location.state?.has_pending_import;

    // Replace the history entry without import state
    const clearImportState = useCallback(() => {
        navigate(location.pathname, { replace: true, state: null });
    }, [navigate, location.pathname]);

    const { isWaitingForUpload } = useUploadDetection({
        orgId,
        activeImportId,
        hasPendingImport,
        onProcessingComplete: () => {
            dispatch(
                discoveryInventoryApi.util.invalidateTags(['DISCOVERY_INVENTORY', 'FILTERED_DISCOVERY_INVENTORY']),
            );
            clearImportState();
        },
        onProcessingFailed: () => {
            clearImportState();
        },
    });

    const containerClassNames = classNames('discovery-inventory-main-container', {
        'discovery-inventory-main-container--drawer-open': drawerOpen,
    });

    const handleRowClick = (record) => {
        setSelectedRowData(record);
        setSelectedRowId(record.id);
        setDrawerOpen(true);
    };

    // Update the selected row data from inventory table whenever table data changes
    const handleInventoryDataUpdate = useCallback(
        (tableData) => {
            if (drawerOpen && selectedRowId && tableData) {
                const updatedRecord = tableData.find((item) => item.id === selectedRowId);
                if (updatedRecord) {
                    setSelectedRowData(updatedRecord);
                }
            }
        },
        [drawerOpen, selectedRowId, setSelectedRowData],
    );

    return (
        <Layout className="discovery-inventory-page">
            <div className={containerClassNames}>
                <NotificationsPanel notifications={notifications} isLoading={isNotificationsLoading} />
                {isWaitingForUpload && (
                    <Alert title="Inventory upload currently in progress..." type="info" showIcon closable={false} />
                )}
                <SummaryWrapper
                    title="Summary"
                    isWaitingForUpload={isWaitingForUpload}
                    onFilterClick={handleCardFilterClick}
                />
                <div ref={tableRef}>
                    <DiscoveryInventoryContainer
                        onRowClick={handleRowClick}
                        selectedRowData={selectedRowData}
                        onInventoryDataUpdate={handleInventoryDataUpdate}
                        initialFilters={cardFilters}
                    />
                </div>
            </div>
            <Sider width={400} collapsedWidth={0} collapsed={!drawerOpen} trigger={null}>
                <DiscoveryInventoryDrawer setDrawerOpen={setDrawerOpen} record={selectedRowData} />
            </Sider>
        </Layout>
    );
};
